<script setup lang="ts">
import { useResizeObserver } from "@vueuse/core";
import { computed, ref } from "vue";
import TextAreaField from "@/design/primitives/TextAreaField.vue";
import RichText from "@/shared/rich-text/RichText.vue";
import { useRateTurn } from "../api/use-rate-turn";
import { toolsFromSteps } from "../composables/activity";
import { approvalsFrom } from "../composables/approvals";
import type { Bubble } from "../composables/use-turn-stream";
import { useAgentsI18n } from "../i18n/use-agents-i18n";
import AgentActivity from "./AgentActivity.vue";
import ApprovalCard from "./ApprovalCard.vue";
import BubbleActions from "./BubbleActions.vue";
import ChatAttachment from "./ChatAttachment.vue";
import NavigateCard from "./NavigateCard.vue";

/**
 * Uma fala da conversa, e o que dá para fazer com ela.
 *
 * O que ela desenha, em ordem: o que foi dito, o que veio ANEXADO, aonde o turno
 * levou a pessoa, o registro por passo, e a barra de ações — que mora em
 * `BubbleActions.vue` desde que este arquivo voltou a passar do teto.
 *
 * O anexo é a metade que faltava. A conversa mostrava o que o agente DISSE e não
 * tinha como entregar o que ele TINHA: pedido um arquivo, ele colava o conteúdo
 * como texto e se oferecia para mandá-lo por WhatsApp. Agora o arquivo é um
 * objeto na conversa, com abrir e baixar.
 */
const props = defineProps<{
	bubble: Bubble;
	/** A última fala da thread — só nela regerar quer dizer alguma coisa. */
	last?: boolean;
	/** Um turno está correndo: nada de reescrever a conversa no meio dele. */
	busy?: boolean;
	/** Há um composer para onde mandar o texto (o Ben, ao lado da conversa).
	 *  No painel solto não há, e um botão que não leva a lugar nenhum é pior. */
	canDraft?: boolean;
	/** A tela sabe apagar falas guardadas (tem a sessão em mãos). */
	canRemove?: boolean;
}>();

const emit = defineEmits<{
	/** Contrato com o lote D (a inbox): nome e carga são exatamente estes. */
	useAsDraft: [text: string];
	regenerate: [];
	resend: [text: string];
	/** O lixinho da barra: a fala guardada que a pessoa mandou apagar. */
	remove: [messageId: string];
}>();

const { t } = useAgentsI18n();

/**
 * O polegar pela rota que já existe (`POST /v3/agents/turns/:id/rating`). Um erro
 * é engolido: uma opinião que não chegou não pode roubar a tela de quem está
 * conversando.
 */
const rateTurn = useRateTurn();

const editing = ref(false);
const editText = ref("");

const isAssistant = computed(() => props.bubble.role === "assistant");
/** O resultado de um subagente sem a linha técnica do topo, que é para o modelo. */
const agentText = computed(() => props.bubble.text.replace(/^\[Background agent [^\]]*\]\n?/, ""));
/** Os passos gravados na mesma forma dos passos ao vivo: o cartão de atividade
 *  não deve saber se olha para um turno a acontecer ou para um de ontem. */
const activityTools = computed(() => toolsFromSteps(props.bubble.steps ?? []));
/** O que este turno preparou e espera um clique — ver `approvals.ts`. */
const approvals = computed(() =>
	isAssistant.value ? approvalsFrom(props.bubble.steps ?? []) : [],
);
/** A barra aparece por causa dos fatos que ela carrega — ou porque é aqui que se
 *  pede de novo, inclusive quando o turno terminou em erro e `at` nem existe. */
const showBar = computed(() => isAssistant.value && (!!props.bubble.at || !!props.last));
const canRegenerate = computed(() => isAssistant.value && !!props.last && !props.busy);

function startEdit(): void {
	editText.value = props.bubble.text;
	editing.value = true;
}

function confirmEdit(): void {
	const text = editText.value.trim();
	if (!text) return;
	editing.value = false;
	emit("resend", text);
}
/** A fala longa da pessoa nasce recolhida; `clamped` é medido, não adivinhado pelo tamanho do texto. */
const userText = ref<HTMLElement | null>(null);
const expanded = ref(false);
const clamped = ref(false);
// Medido a cada mudança de tamanho: a gaveta monta escondida (`v-show`) e ali tudo mede zero.
useResizeObserver(userText, () => {
	const el = userText.value;
	if (el && !expanded.value) clamped.value = el.scrollHeight > el.clientHeight + 1;
});
</script>

<template>
	<div>
		<!-- ── O QUE UM SUBAGENTE DEVOLVEU ─────────────────────────────────────
		     Nem a pessoa nem o Ben: o resultado de um trabalho em segundo plano, que
		     o Ben lê e responde logo abaixo. -->
		<div v-if="bubble.role === 'agent'" class="max-w-[92%] space-y-1 rounded-lg border border-border px-3 py-2">
			<p class="text-2xs font-medium text-fg-subtle">{{ t("chat.fromAgent") }}</p>
			<div class="text-sm leading-6 text-fg"><RichText :text="agentText" /></div>
		</div>

		<!-- ── O QUE A PESSOA DISSE ─────────────────────────────────────────── -->
		<div v-else-if="!isAssistant" class="group/bubble ml-auto max-w-[85%] space-y-1">
			<div
				v-if="!editing"
				class="rounded-2xl bg-bg-muted px-4 py-3 text-[1.0625rem] leading-7 text-fg"
			>
				<!-- O que foi anexado. `images` era preenchido no envio e nunca desenhado:
				     a thread mostrava a pergunta e escondia metade do que foi perguntado. -->
				<div v-if="bubble.images?.length" class="mb-1.5 flex flex-wrap gap-1.5">
					<img
						v-for="(src, i) in bubble.images"
						:key="i"
						:src="src"
						:alt="t('chat.sentImage')"
						class="size-16 rounded-md border border-border object-cover"
					/>
				</div>
				<!-- A voz TOCÁVEL junto da transcrição, como na V2. Aqui é o envio ao
				     vivo (object URL); depois do F5 o mesmo áudio volta como anexo da
				     fala, guardado pelo servidor. -->
				<audio
					v-if="bubble.audio"
					:src="bubble.audio"
					controls
					class="mb-1.5 h-9 w-56 max-w-full"
				/>
				<!-- Um texto colado (uma cláusula, um e-mail) empurrava a resposta para
				     fora da tela: recolhido em oito linhas, como no app do Claude. -->
				<div ref="userText" class="whitespace-pre-wrap" :class="expanded ? '' : 'line-clamp-8'">{{ bubble.text }}</div>
				<button
					v-if="clamped"
					type="button"
					class="mt-1 text-xs font-medium text-fg-subtle underline-offset-2 hover:underline"
					:aria-expanded="expanded"
					@click="expanded = !expanded"
				>
					{{ expanded ? t("chat.showLess") : t("chat.showMore") }}
				</button>
			</div>

			<!-- A nota de voz RELEMBRADA (F5): o servidor guardou o áudio como anexo
			     da fala, e o cartão sabe tocá-lo. Só existia no lado do assistente. -->
			<div v-if="!editing && bubble.attachments?.length" class="space-y-1.5">
				<ChatAttachment v-for="file in bubble.attachments" :key="file.id" :file="file" />
			</div>

			<!--
				A CAIXA DE CORRIGIR — só quando alguém pediu para corrigir.

				Ela era o `v-else` do bloco de anexos acima, e um `v-else` responde à
				condição INTEIRA do irmão: `!editing && attachments?.length`. Numa
				pergunta sem anexo (que é quase toda pergunta) essa condição é falsa
				sem ninguém estar a editar coisa nenhuma — então a caixa de edição, o
				"cancelar" e o "reenviar" ficavam abertos por baixo de CADA fala da
				pessoa, para sempre. É o "campo vazio já aberto embaixo" que o dono
				relatou, na metade que não vinha do cartão de atividade.
			-->
			<div v-else-if="editing" class="space-y-1">
				<TextAreaField
					v-model="editText"
					:rows="3"
					class="text-sm"
					:aria-label="t('chat.editQuestion')"
				/>
				<div class="flex justify-end gap-2 text-2xs">
					<button type="button" class="text-fg-subtle hover:text-fg" @click="editing = false">
						{{ t("chat.cancelEdit") }}
					</button>
					<button type="button" class="text-accent hover:underline" @click="confirmEdit">
						{{ t("chat.resend") }}
					</button>
				</div>
			</div>

			<!-- A barra da PERGUNTA: a hora, corrigir e copiar. Ela era um link solto
			     "editar e reenviar" e nada mais — a fala da pessoa não tinha relógio,
			     e a da V2 tem. Some no hover como a do assistente. -->
			<BubbleActions
				v-if="!editing"
				:bubble="bubble"
				:can-edit="!busy"
				:can-remove="canRemove"
				@edit="startEdit"
				@remove="emit('remove', $event)"
			/>
		</div>

		<!-- ── O QUE O AGENTE RESPONDEU ─────────────────────────────────────── -->
		<div v-else class="group/bubble max-w-[92%] space-y-1.5">
			<!-- ── O QUE ELE FEZ PARA CHEGAR ALI ───────────────────────────────
			     Raciocínio e ferramentas ACIMA da resposta, como no Claude (dono, 25/10). Eram três
			     peças com regras próprias — uma gaveta `<details>`, uma lista
			     `ToolSteps` e um número solto de tokens na barra —, e é UM turno.
			     Quem decide o que aparece é a política de exibição, nunca este
			     ficheiro: ver `AgentActivity.vue`. -->
			<AgentActivity
				part="steps"
				:tools="activityTools"
				:thinking="bubble.thinking ?? ''"
				:meter="bubble.meter ?? null"
			/>
			<p v-if="bubble.refused && bubble.role === 'assistant'" class="text-xs italic text-fg-subtle">
				{{ t("chat.refused") }}
			</p>
			<p v-else-if="bubble.silent" class="text-xs italic text-fg-subtle">
				{{ t("chat.silent") }}
			</p>
			<div
				v-else
				class="rounded-lg rounded-bl-sm py-2"
				:class="bubble.failed ? 'bg-danger-subtle px-3 text-sm text-danger' : 'font-serif text-[1.0625rem] leading-7 text-fg'"
			>
				<RichText v-if="!bubble.failed" :text="bubble.text" />
				<span v-else>{{ bubble.text }}</span>
			</div>

			<!-- O que o turno ENTREGOU. Um por linha e não em grade: o nome do arquivo
			     é a informação, e um nome truncado a três palavras não é um nome. -->
			<div v-if="bubble.attachments?.length" class="space-y-1.5">
				<ChatAttachment v-for="file in bubble.attachments" :key="file.id" :file="file" />
			</div>

			<!-- Aonde o turno levou a pessoa. Fica aqui depois que a rota mudou. -->
			<NavigateCard v-for="(target, i) in bubble.navigate ?? []" :key="i" :target="target" />

			<ApprovalCard v-for="a in approvals" :key="`${a.kind}:${a.id}`" :approval="a" />

			<!-- A conta do turno, sob a resposta: modelo · tokens · créditos · custo. -->
			<AgentActivity part="meter" :tools="activityTools" :meter="bubble.meter ?? null" :tokens="bubble.usage ?? null" />

			<!-- A virada parou À ESPERA DE UMA PESSOA. Sem esta linha, um turno que
			     pediu ajuda humana lê-se como um turno que acabou. -->
			<p v-if="bubble.awaitsHuman" class="text-2xs text-fg-muted">
				{{ t("live.awaitsHuman") }}
			</p>

			<BubbleActions
				v-if="showBar"
				:bubble="bubble"
				:can-draft="canDraft"
				:can-regenerate="canRegenerate"
				:can-remove="canRemove"
				@use-as-draft="emit('useAsDraft', $event)"
				@regenerate="emit('regenerate')"
				@remove="emit('remove', $event)"
				@rate="rateTurn.mutate({ turnId: bubble.turnId ?? '', ...$event })"
			/>
		</div>
	</div>
</template>
